import React from 'react';

type DynamicJSONCardProps = {
  header: string;
  content: any;
};

const DynamicJSONCard = ({ header, content }: DynamicJSONCardProps) => {
  // Helper to render array values as individual tags
  const renderArrayAsTags = (array: any[]) => (
    <span className="flex inline-flex flex-wrap gap-1">
      {array.map((item, i) => (
        <span key={i} className="rounded bg-teal-t10 px-2 py-1 font-normal">
          {/* true, false and null aren't rendered by React on their own. */}
          {String(item)}
        </span>
      ))}
    </span>
  );

  // Helper to render boolean values as checkboxes
  const renderBoolean = (value: boolean) => (
    <input
      type="checkbox"
      checked={value}
      readOnly
      className="mb-1 cursor-default self-end accent-greyscale-5"
    />
  );

  // A person's name and username read as one line: "Victor L (victorl@…)".
  const mergeNameAndUsername = (data: object): [string, any][] => {
    const entries = Object.entries(data);
    const keyOf = (names: string[]) =>
      entries.find(([k]) => names.includes(k.toLowerCase()))?.[0];
    const nameKey = keyOf(['full name', 'full_name', 'name']);
    const usernameKey = keyOf(['username']);
    if (!nameKey || !usernameKey) return entries;
    const name = (data as any)[nameKey];
    const username = (data as any)[usernameKey];
    if (typeof name !== 'string' || typeof username !== 'string')
      return entries;
    return entries
      .filter(([k]) => k !== usernameKey)
      .map(([k, v]) => (k === nameKey ? [k, `${name} (${username})`] : [k, v]));
  };

  // Function to render content based on its type
  const renderContent = (data: any) => {
    // A list of plain values shows as tags. Checked before objects: an array is
    // an object too, and would otherwise list each item under its index. A list
    // of records keeps the structured layout below, since a record can't be a tag.
    if (
      Array.isArray(data) &&
      data.every((item) => item === null || typeof item !== 'object')
    ) {
      return <div className="w-full">{renderArrayAsTags(data)}</div>;
    } else if (typeof data === 'object' && data !== null) {
      return (
        <div className="w-full">
          {mergeNameAndUsername(data).map(([key, value], index, arr) => (
            <div
              key={index}
              className={`py-3 ${index !== arr.length - 1 ? 'border-b border-ui-divider-lines' : ''}`}
            >
              {Array.isArray(value) ? (
                <div className="flex items-baseline">
                  <span className="mr-2 font-normal">
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </span>
                  {renderArrayAsTags(value)}
                </div>
              ) : typeof value === 'object' && value !== null ? (
                <div>
                  <div className="font-normal">
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </div>
                  <div className="ml-4 mt-2">
                    {Object.entries(value as object).map(
                      ([subKey, subValue], subIndex, subArr) => (
                        <div
                          key={subIndex}
                          className={`pb-2 ${subIndex !== subArr.length - 1 ? 'mb-2 border-b border-ui-divider-lines' : ''}`}
                        >
                          {typeof subValue === 'boolean' ? (
                            <div className="flex items-end">
                              {renderBoolean(subValue)}
                              <span className="ml-2 font-normal">
                                {subKey.charAt(0).toUpperCase() +
                                  subKey.slice(1)}
                              </span>
                            </div>
                          ) : (
                            <>
                              <span className="mr-2 font-normal">
                                {subKey.charAt(0).toUpperCase() +
                                  subKey.slice(1)}
                              </span>
                              {Array.isArray(subValue) ? (
                                renderArrayAsTags(subValue)
                              ) : (
                                <span className="font-normal text-ui-cta-primary">
                                  {String(subValue)}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex w-full items-start">
                  {typeof value === 'boolean' ? (
                    <>
                      {renderBoolean(value)}
                      <span className="ml-2 font-normal">
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="mr-2 self-start font-normal">
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </span>
                      <span className="self-start font-normal text-ui-text-primary-text">
                        {String(value)}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    } else {
      return typeof data === 'boolean' ? (
        renderBoolean(data)
      ) : (
        <p className="w-full font-normal text-ui-cta-primary">{String(data)}</p>
      );
    }
  };

  return (
    <div className="flex w-full flex-col items-start text-body-sm">
      <h3 className="text-body-md font-normal text-ui-text-primary-text">
        {header}
      </h3>
      <div className="mt-2 flex w-full flex-col items-start justify-start">
        {renderContent(content)}
      </div>
    </div>
  );
};

export default DynamicJSONCard;
